/* eslint-disable max-lines */
'use client'

import dynamic from 'next/dynamic'

import { useEffect, useRef, useState } from 'react'

import { LandingFooter } from '@/components/LandingFooter'
import { ScrollContainer } from '@/components/ScrollContainer'
import { HeroSection } from '@/components/Sections/HeroSection'
import { IS_GERMAN } from '@/constants'

// Lazy load all section components
const SavingsSection = dynamic(() => import('@/components/Sections/SavingsSection').then(mod => mod.SavingsSection), {
  ssr: false,
})

const OnboardingSection = dynamic(
  () => import('@/components/Sections/OnboardingSection').then(mod => mod.OnboardingSection),
  { ssr: false },
)

const TransparencySection = dynamic(
  () => import('@/components/Sections/TransparencySection').then(mod => mod.TransparencySection),
  { ssr: false },
)

const HighlightsSection = dynamic(
  () => import('@/components/Sections/HighlightsSection').then(mod => mod.HighlightsSection),
  { ssr: false },
)

const FinalEngagementSection = dynamic(
  () => import('@/components/Sections/FinalEngagementSection').then(mod => mod.FinalEngagementSection),
  { ssr: false },
)

export default function Home() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentSection, setCurrentSection] = useState(0)
  const prevDeltaRef = useRef(0)
  const isScrollingRef = useRef(false)
  const userInteractingRef = useRef(false)
  const lastWheelTimeRef = useRef(0)
  const wheelEventsRef = useRef<Array<{ delta: number; time: number }>>([])
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isTrackpadMomentum = (
    wheelEvents: Array<{ delta: number; time: number }>,
    timeSinceLastWheel: number,
  ): boolean => {
    if (wheelEvents.length < 3) return false

    // Calculate average time between events
    const timeDiffs = wheelEvents.slice(1).map((evt, i) => evt.time - wheelEvents[i].time)
    const avgTimeDiff = timeDiffs.reduce((a, b) => a + b, 0) / timeDiffs.length

    // Calculate average delta
    const avgDelta = wheelEvents.reduce((a, b) => a + Math.abs(b.delta), 0) / wheelEvents.length

    // Trackpad characteristics:
    // 1. Very consistent timing between events (usually 16-17ms for 60Hz)
    // 2. Smaller, more consistent delta values
    // 3. Events come in quick succession
    return (
      avgTimeDiff < 20 && // Consistent timing
      avgDelta < 15 && // Smaller deltas
      timeSinceLastWheel < 50
    ) // Quick succession
  }

  useEffect(() => {
    const isLargeScreen = window.innerHeight >= 750 && window.innerWidth >= 1280
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1
    if (!isLargeScreen || isFirefox) return

    const container = containerRef.current
    if (!container) return

    const sectionHeight = window.innerHeight
    const totalSections = container.children.length

    // Function to scroll to a specific section with native smooth scrolling
    const scrollToSection = (sectionIndex: number): void => {
      if (sectionIndex === currentSection || isScrollingRef.current) return

      isScrollingRef.current = true
      setCurrentSection(sectionIndex)
      const targetScrollTop = sectionIndex * sectionHeight

      try {
        container.scrollTo({ behavior: 'smooth', top: targetScrollTop })

        setTimeout(() => {
          isScrollingRef.current = false
        }, 500)
      } catch {
        container.scrollTop = targetScrollTop
        isScrollingRef.current = false
      }
    }

    // Track when scrolling completes to update current section
    const handleScroll = (): void => {
      if (!userInteractingRef.current) return

      const newSection = Math.round(container.scrollTop / sectionHeight)

      if (newSection !== currentSection && !isScrollingRef.current) {
        setCurrentSection(newSection)
      }
    }

    // Clear wheel event tracking after debounce period
    const clearWheelTracking = (): void => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)

      scrollTimeoutRef.current = setTimeout(() => {
        wheelEventsRef.current = []
        scrollTimeoutRef.current = null
      }, 350)
    }

    // Handle manual wheel events with enhanced trackpad detection and dampening
    const handleWheel = (e: WheelEvent): void => {
      e.preventDefault()
      if (isScrollingRef.current) return

      userInteractingRef.current = true
      const now = Date.now()
      const delta = e.deltaY

      // Track wheel events for analyzing patterns
      wheelEventsRef.current.push({ delta, time: now })
      if (wheelEventsRef.current.length > 5) wheelEventsRef.current.shift()

      const timeSinceLastWheel = now - lastWheelTimeRef.current
      lastWheelTimeRef.current = now

      // Reset if it's been a while since last wheel event
      if (timeSinceLastWheel > 350) {
        prevDeltaRef.current = 0
        clearWheelTracking()
        return
      }

      // Direction detection and change handling
      const direction = Math.sign(delta)

      if (direction !== 0 && prevDeltaRef.current !== 0 && direction !== Math.sign(prevDeltaRef.current)) {
        prevDeltaRef.current = 0
        wheelEventsRef.current = [{ delta, time: now }]
        return
      }

      const isTrackpad = isTrackpadMomentum(wheelEventsRef.current, timeSinceLastWheel)

      // Apply dampening based on event pattern
      if (wheelEventsRef.current.length >= 3 && timeSinceLastWheel < 50) {
        prevDeltaRef.current += delta * (isTrackpad ? 0.08 : 1)
      } else {
        prevDeltaRef.current += delta * 0.8
      }

      const threshold = isTrackpad ? 200 : 30
      const accumulatedThreshold = isTrackpad ? 120 : 20

      if (Math.abs(delta) > threshold || Math.abs(prevDeltaRef.current) > accumulatedThreshold) {
        const targetSection = Math.max(0, Math.min(totalSections - 1, currentSection + direction))

        if (targetSection !== currentSection) {
          scrollToSection(targetSection)
          prevDeltaRef.current = 0
          wheelEventsRef.current = []
        }
      }

      clearWheelTracking()
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (isScrollingRef.current) return

      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault()
        scrollToSection(Math.min(currentSection + 1, totalSections - 1))
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault()
        scrollToSection(Math.max(currentSection - 1, 0))
      } else if (e.key === 'Home') {
        e.preventDefault()
        scrollToSection(0)
      } else if (e.key === 'End') {
        e.preventDefault()
        scrollToSection(totalSections - 1)
      }
    }

    // Add event listeners
    container.addEventListener('wheel', handleWheel, { passive: false })
    container.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
      container.removeEventListener('wheel', handleWheel)
      container.removeEventListener('scroll', handleScroll)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentSection])

  return (
    <div
      ref={containerRef}
      className="snap-container"
      style={{ overscrollBehavior: 'none', scrollSnapType: 'y mandatory' }}
    >
      <div id="hero-section" data-section-index="hero" className="snap-section flex justify-center items-center">
        <HeroSection isGerman={IS_GERMAN} setCurrentSection={setCurrentSection} />
      </div>
      {!IS_GERMAN && (
        <>
          <div id="section-1" data-section-index="chart" className="snap-section flex justify-center items-center">
            <ScrollContainer>
              <SavingsSection />
            </ScrollContainer>
          </div>
          <div data-section-index="start" className="snap-section flex justify-center items-center">
            <ScrollContainer>
              <OnboardingSection />
            </ScrollContainer>
          </div>
          <div data-section-index="transparency" className="snap-section flex justify-center items-center">
            <ScrollContainer>
              <TransparencySection />
            </ScrollContainer>
          </div>
          <div data-section-index="highlights" className="snap-section flex justify-center items-center">
            <ScrollContainer>
              <HighlightsSection />
            </ScrollContainer>
          </div>
          <div data-section-index="footer" className="snap-section flex flex-col justify-end items-center">
            <ScrollContainer>
              <FinalEngagementSection />
            </ScrollContainer>
            <LandingFooter />
          </div>
        </>
      )}
      {IS_GERMAN && (
        <div className="mt-10">
          <LandingFooter />
        </div>
      )}
    </div>
  )
}
